import {configConstants, getConfigItem} from '@servicenow/ui-config';
import {shouldCache} from './shouldCache';
import {getUserSysId, makeLRUCachePolicy, makePersistentCache} from '@servicenow/ui-effect-helpers';
import {getGraphQLDocument, isSubscriptionResult} from '../utils';
import documentToQueryString from './documentToQueryString';

const {GQL_CACHE_MAX_PERSIST, GQL_CACHE_CAPACITY, GQL_CACHING_ENABLED} = configConstants;

let persistantCache;

const getCache = async function () {
	if (persistantCache) {
		return persistantCache;
	}
	try {
		persistantCache = await makePersistentCache({
			dbName: 'Seismic',
			storeName: 'gqlQueries',
			schemaVersion: 1,
			options: {cachePolicy: makeLRUCachePolicy(getConfigItem(GQL_CACHE_CAPACITY))}
		});
	} catch (e) {
		return null;
	}
	return persistantCache;
};

export async function clearGraphQLResultsCache() {
	await (await getCache())?.clear();
}

export function shouldDoResultCaching(operation) {
	const {transactionMetaData} = operation;
	return (
		getConfigItem(GQL_CACHING_ENABLED) &&
		transactionMetaData?.cacheable &&
		shouldCache(transactionMetaData?.query, globalThis)
	);
}

// @TODO: add Domain info to cacheQueryKey
const getCacheQueryKey = ({query, variables, params = {}}) => {
	let resolvedQuery = query,
		resolvedVariables = variables,
		resolvedParams = params;
	if (typeof resolvedQuery !== 'string') resolvedQuery = JSON.stringify(resolvedQuery);

	if (typeof resolvedVariables !== 'string') resolvedVariables = JSON.stringify(resolvedVariables);

	if (typeof resolvedParams !== 'string') resolvedParams = JSON.stringify(resolvedParams);

	const userToken = getUserSysId(globalThis);
	return `${resolvedQuery}:${resolvedVariables}:${resolvedParams}:${userToken}`;
};

export async function checkCacheForResult(operation, batchItem, fetchFunc) {
	const {
		operationName,
		variables,
		templateVars,
		domainContext,
		transactionMetaData: {query, params} = {}
	} = operation;
	const gqlDocument = getGraphQLDocument(query, templateVars, domainContext, operationName);
	const queryString = documentToQueryString(gqlDocument);
	const cacheQueryKey = getCacheQueryKey({query: queryString, variables, params});

	const cache = await getCache();
	const isCached = cache ? await cache.has(cacheQueryKey) : false;

	if (isCached) {
		const refetchPromise = () => {
			return makeRefetchRequest([batchItem], fetchFunc);
		};
		const cachedResult = await cache.get(cacheQueryKey);
		const {headers} = cachedResult;
		const cachedDate = new Date(headers?.date);

		if (cachedDate) {
			const minutesSinceCached = (Date.now() - cachedDate) / 1000 / 60;

			if (minutesSinceCached < (getConfigItem(GQL_CACHE_MAX_PERSIST) || 2880)) {
				return {
					isCachedResult: true,
					refetchPromise,
					...cachedResult
				};
			}
		}
	}
}

//For refetch requests clear out any streaming since it is not needed as the request will happen in the background
//after the main request
function makeRefetchRequest(batchItems = [], fetchFunc) {
	batchItems.forEach(currItem => currItem?.operation?.queryContext?.clearStreaming());
	return fetchFunc(batchItems);
}

export async function storeResultInCache(operation, result, headers) {
	const {query, variables, cacheable, transactionMetaData: {params} = {}} = operation;
	const {data = null, errors = []} = result;
	const payload = {data, errors, headers};

	// Check equality ?
	if (
		getConfigItem(GQL_CACHING_ENABLED) &&
		!query.startsWith('mutation') &&
		cacheable &&
		!isSubscriptionResult(result)
	) {
		const cache = await getCache();
		if (cache) {
			const cacheQueryKey = getCacheQueryKey({query, variables, params});
			await cache.set(cacheQueryKey, payload);
			requestIdleCallback(() => cache.persist());
		}
	}
}
